import castErrorToBaseError from '@/functions/castErrorToBaseError';
import EmailRecipientInputCard from '@/modules/email-settings/components/email-recipient-input-card';
import useRecipientSettingsQuery from '@/modules/email-settings/queries/useRecipientSettingsQuery';
import type {
  RecipientSettings,
  RecipientSettingsKey,
} from '@/modules/email-settings/types/RecipientSettings';
import { createFileRoute, notFound } from '@tanstack/react-router';
import {
  Card,
  Empty,
  Skeleton,
  Typography,
  useNotification,
} from '@thisisdmg/tirecloud-patterns/v2';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './index.module.css';
import config from '@/modules/config/functions/config';

function EmailRecipientSettingPage() {
  const { t } = useTranslation();
  const notification = useNotification();

  const { data, isFetching, isError, error } = useRecipientSettingsQuery();

  useEffect(() => {
    if (isError) {
      const e = castErrorToBaseError(error);
      notification.error({
        key: e.id,
        message: `0x${e.id.toString(16).toUpperCase()}`,
        description: t(`errors.gen.${e.path}`, { errorId: e.id }),
      });
    }
  }, [error, isError, notification, t]);

  return (
    <div>
      <div className={classes.email_settings_header}>
        <div>
          <Typography
            variant="hero"
            className={classes.email_settings_header__title}
          >
            {t('email_settings.title')}
          </Typography>
        </div>
      </div>
      {isFetching && !data ? (
        <Skeleton active />
      ) : !data ? (
        <Card>
          <Empty />
        </Card>
      ) : (
        <>
          <div className={classes.email_settings_list}>
            {(
              Object.entries(data) as Array<
                [
                  keyof RecipientSettings,
                  RecipientSettings[keyof RecipientSettings],
                ]
              >
            )
              .filter(([name]) => name !== 'id')
              .map(([name, values]) => {
                return (
                  <EmailRecipientInputCard
                    key={name}
                    name={name as RecipientSettingsKey}
                    values={values as string[]}
                    originalValues={data}
                  />
                );
              })}
          </div>
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute(
  '/_main-ui/_protected/email-settings/' as any
)({
  component: EmailRecipientSettingPage,
  loader: async () => {
    if (config('features.emailRecipientsSetting.enabled') !== true)
      throw notFound();
  },
});
